import { z } from 'zod'
import { browserEnvironmentSchema } from '../common/browser-environment'
import { AdminApiError, request } from '../api/client'

const summarySchema = z.object({
  browserEnvironment: browserEnvironmentSchema.nullable().default(null),
  id:z.string(), chatId:z.string(), userId:z.string(), student:z.string(), email:z.string(),
  clientId:z.string(), client:z.string(), cohortId:z.string().nullable(), cohort:z.string().nullable(),
  sessionType:z.enum(['lesson','onboarding']), tutor:z.string().nullable(), lesson:z.string().nullable(),
  tutorContentKey:z.string().nullable(),
  level:z.string().nullable(), gutRating:z.number().int().min(1).max(5), technicalIssue:z.boolean(),
  criticality:z.enum(['none','low','medium','high']), actionNeeded:z.boolean(), triageReasons:z.array(z.string()),
  commentPreview:z.string().nullable(), createdAt:z.string(),
})
const answersSchema = z.object({
  difficulty:z.string(),jpAmount:z.string(),speakingSpeed:z.string().nullable(),
  transcriptionAccuracy:z.string().nullable(),coachHearing:z.string().nullable(),
  recommendedLevelVerdict:z.string().nullable(),first60Seconds:z.string().nullable(),
  bestMoment:z.string().nullable(),worstMoment:z.string().nullable(),notes:z.string().nullable(),
  deliberatelyTooHard:z.boolean().nullable(),coachOfferedEasier:z.boolean().nullable(),
})
const listSchema=z.object({items:z.array(summarySchema),total:z.number(),page:z.number(),limit:z.number()})
const detailSchema=z.object({
  summary:summarySchema,answers:answersSchema,
  callStartedAt:z.string().nullable().default(null),
  callDurationMs:z.number().int().nonnegative().nullable().default(null),
  callEnrollmentId:z.string().nullable().default(null),
})
export type FeedbackSummary=z.infer<typeof summarySchema>
export type FeedbackList=z.infer<typeof listSchema>
export type FeedbackDetail=z.infer<typeof detailSchema>
const dateSchema=z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(value => {
  const parsed=new Date(`${value}T00:00:00Z`)
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0,10)===value && value>'0001-01-01' && value<'9999-12-31'
},'Check the submitted date range.')
const boolSchema=z.enum(['true','false']).transform(value => value==='true').optional()
const paramsSchema=z.object({
  clientId:z.guid().optional(),cohortId:z.guid().optional(),
  sessionType:z.enum(['lesson','onboarding']).optional(),rating:z.coerce.number().int().min(1).max(5).optional(),
  technicalIssue:boolSchema,hasComments:boolSchema,actionNeeded:boolSchema,criticality:z.enum(['none','low','medium','high']).optional(),fromDate:dateSchema.optional(),toDate:dateSchema.optional(),
  sortField:z.enum(['createdAt','gutRating','criticality']).default('createdAt'),sortDirection:z.enum(['asc','desc']).default('desc'),
  page:z.coerce.number().int().min(1).max(100000).default(1),
}).refine(value => !value.fromDate || !value.toDate || value.fromDate<=value.toDate,{message:'Start date must be on or before end date.'})
export type FeedbackParams=z.output<typeof paramsSchema>
export const FEEDBACK_FILTERS=['clientId','cohortId','sessionType','rating','technicalIssue','hasComments','actionNeeded','criticality','fromDate','toDate'] as const
export const FEEDBACK_URL_KEYS=[...FEEDBACK_FILTERS,'sortField','sortDirection','page'] as const
export function feedbackParams(source:URLSearchParams) {
  return paramsSchema.safeParse(Object.fromEntries(FEEDBACK_URL_KEYS.map(key => [key,source.get(key)||undefined])))
}
export function feedbackListSearch(source:URLSearchParams):string {
  const result=new URLSearchParams()
  for(const key of FEEDBACK_URL_KEYS) { const value=source.get(key); if(value) result.set(key,value) }
  return result.toString() ? `?${result}` : ''
}
export const feedbackClient={
  list:async (params:FeedbackParams,signal?:AbortSignal):Promise<FeedbackList> => {
    const search=new URLSearchParams()
    for(const [key,value] of Object.entries(params)) if(value!==undefined) search.set(key,String(value))
    return listSchema.parse(await request<unknown>(`/coach/admin/feedback?${search}`,{cache:'no-store',signal}))
  },
  detail:async (id:string,signal?:AbortSignal):Promise<FeedbackDetail> => {
    try {
      return detailSchema.parse(await request<unknown>(`/coach/admin/feedback/${encodeURIComponent(id)}`,{cache:'no-store',signal}))
    } catch(error) {
      if(error instanceof AdminApiError && error.status===422) throw new AdminApiError('NOT_FOUND','Feedback unavailable',404,'feedback_not_found')
      throw error
    }
  },
}
export function feedbackUnavailable(error:unknown):boolean {
  return error instanceof AdminApiError && error.detail==='feedback_not_found'
}
export function messageForFeedbackError(error:unknown):string {
  if(error instanceof AdminApiError) {
    if(error.detail==='feedback_not_found') return 'This feedback is unavailable or has been deleted.'
    if(error.status===403) return 'An internal admin account is required to inspect feedback.'
    if(error.status===422) return 'Check the selected filters and date range, then try again.'
  }
  return 'Could not load feedback. Try again.'
}
export function feedbackDate(value:string):string {
  return new Intl.DateTimeFormat('en-GB',{timeZone:'Asia/Tokyo',year:'numeric',month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}).format(new Date(value))
}
export function answerLabel(value:string|boolean|null):string {
  if(value===null) return 'Not recorded'
  if(typeof value==='boolean') return value?'Yes':'No'
  const labels: Record<string,string> = {about_right:'About right',too_easy:'Too easy',too_hard:'Too hard',too_little:'Too little',too_much:'Too much',too_slow:'Too slow',too_fast:'Too fast',perfect:'Accurate',had_mistakes:'Had mistakes',always_heard:'Always heard',had_to_repeat:'Had to repeat',too_low:'Too low',right:'About right',too_high:'Too high'}
  return Object.hasOwn(labels,value) ? labels[value]! : value
}

export function criticalityLabel(value:FeedbackSummary['criticality']):string {
  return ({none:'None',low:'Low',medium:'Medium',high:'High'})[value]
}
export function triageRecommendation(row:Pick<FeedbackSummary,'criticality'|'actionNeeded'>):string {
  return row.actionNeeded?'Action needed':row.criticality==='low'?'Review':'No action indicated'
}
